// src/components/BitacoraAutocomplete.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Alert
} from '@mui/material';
import { useSftfApi } from '../api/sftfClient';
import { useAuth } from '../context/AuthContext';

// Tiempo de espera desde la última tecla antes de disparar la búsqueda. Sin
// esto, escribir un folio de 6 dígitos dispara seis peticiones reales (una
// por dígito), cada una con reintentos propios (~1.9 s c/u), y la respuesta
// de un dígito corto puede llegar después de la del folio completo.
const DEBOUNCE_MS = 300;

const BitacoraAutocomplete = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  touched,
  fullWidth = true,
  disabled = false,
  size = "medium"
}) => {
  const { auth } = useAuth();
  const { buscarBitacoras } = useSftfApi(auth);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);

  // Sincronizar inputValue con el valor seleccionado
  const prevValueRef = React.useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      if (value) {
        const label = typeof value === 'string'
          ? value
          : `${value.claveBitacora} - ${value.serieBitacora}${value.folioBitacora}`;
        setInputValue(label);
      } else if (value === null || value === undefined) {
        setInputValue('');
      }
      prevValueRef.current = value;
    }
  }, [value]);

  // Guardia contra respuestas obsoletas: si el usuario ya escribió más
  // dígitos, la respuesta de una búsqueda anterior (por ejemplo, la de "31"
  // llegando después que la de "310801") se descarta en vez de pisar las
  // opciones vigentes. Mismo patrón que ConceptoAutocomplete.
  const activeSearchRef = useRef(0);
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const ejecutarBusqueda = useCallback(async (texto) => {
    const currentSearchId = ++activeSearchRef.current;
    try {
      setLoading(true);
      setErrorMessage(null);
      const bitacoras = await buscarBitacoras(texto);

      if (activeSearchRef.current !== currentSearchId) return; // hay una búsqueda más reciente

      setOptions(Array.isArray(bitacoras) ? bitacoras : []);
    } catch (err) {
      if (activeSearchRef.current !== currentSearchId) return;
      console.error('Error al buscar bitácoras:', err);
      const errorMsg = err?.response?.data?.message || err?.message || 'Error al buscar bitácoras. Por favor, intente nuevamente.';
      setErrorMessage(errorMsg);
      setOptions([]);
    } finally {
      if (activeSearchRef.current === currentSearchId) {
        setLoading(false);
      }
    }
  }, [buscarBitacoras]);

  // Buscar bitácoras cuando el usuario escribe, con debounce para no mandar
  // una petición real por cada tecla.
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (newInputValue.length === 0) {
      // buscarBitacoras('') siempre devuelve [] (no es un folio numérico), así
      // que no vale la pena llamarla: solo se limpian las opciones y se
      // invalida cualquier búsqueda pendiente.
      activeSearchRef.current += 1;
      setLoading(false);
      setErrorMessage(null);
      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      ejecutarBusqueda(newInputValue);
    }, DEBOUNCE_MS);
  };

  const handleChange = (event, newValue) => {
    console.log('BitacoraAutocomplete - onChange:', newValue, 'Tipo:', typeof newValue);
    onChange(name, newValue);
  };

  const handleBlur = (event) => {
    // Si el usuario escribió texto libre y no seleccionó una opción,
    // capturar el inputValue como el valor
    if (inputValue && inputValue.trim() !== '') {
      // Verificar si el valor actual es diferente del inputValue
      const currentValueStr = typeof value === 'string' 
        ? value 
        : value 
          ? `${value.claveBitacora} - ${value.serieBitacora}${value.folioBitacora}`
          : '';
      
      // Si el inputValue es diferente del valor actual, actualizar con el texto libre
      if (inputValue.trim() !== currentValueStr.trim()) {
        console.log('BitacoraAutocomplete - onBlur: Capturando texto libre:', inputValue);
        onChange(name, inputValue.trim());
      }
    }
    
    // Llamar al onBlur original si existe
    if (onBlur) {
      onBlur(event);
    }
  };

  return (
    <Box>
      <Autocomplete
        options={Array.isArray(options) ? options : []}
        value={value}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        onBlur={handleBlur}
        freeSolo={true}
        sx={{ minHeight: '56px', minWidth: '200px' }}
        disabled={disabled}
        fullWidth={fullWidth}
        loading={loading}
        noOptionsText={inputValue.trim() === '' ? "Escribe el folio de la bitácora" : "No se encontraron bitácoras con ese folio"}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option ? `${option.claveBitacora} - ${option.serieBitacora}${option.folioBitacora}` : '';
        }}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return option.claveBitacora === value.claveBitacora;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={touched && Boolean(error)}
            helperText={touched && error}
            size={size}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Box>
                  {loading ? <CircularProgress color="primary" size={20} sx={{ mr: 1 }} /> : null}
                  {params.InputProps.endAdornment}
                </Box>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          console.log('RenderOption - bitacora:', option);
          
          // Validar que option existe y es un objeto
          if (!option || typeof option !== 'object') {
            return (
              <Box component="li" {...props}>
                <Box>Opción inválida</Box>
              </Box>
            );
          }
          
          return (
            <Box component="li" {...props}>
              <Box>
                <Box sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                  {option.claveBitacora || 'Sin clave'} - {option.serieBitacora || ''}{option.folioBitacora || ''}
                </Box>
                <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mt: 0.5 }}>
                  {option.origenNombre || 'Sin origen'} → {option.destinoNombre || 'Sin destino'}
                </Box>
                <Box sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 0.5 }}>
                  Operador: {option.operadorNombre || 'N/A'}
                </Box>
              </Box>
            </Box>
          );
        }}
        loadingText="Buscando bitácoras..."
      />
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}
    </Box>
  );
};

export default BitacoraAutocomplete;
